import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { CheckMapAPIKey } from '../components/CheckMapAPIKey';
import { ClinicCard } from '../components/ClinicCard';
import { IClinic, useClinics } from '../hooks/useClinics';
import { useHandleGeocodeCurrentLocation } from '../map/hooks/useHandleGeocodeCurrentLocation';
import { useHandleGeocodeSearchInput } from '../map/hooks/useHandleGeocodeSearchInput';
import { AutoComplete } from '../map/Map.AutoComplete';
import { uniqueSortedCountriesSelector } from '../store/selectors';
import { useLocatorStore } from '../store/useLocatorStore';

export function SearchPage(): ReactElement {
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const mapViewBounds = useLocatorStore((state) => state.mapViewBounds);
  const { isPending, isError, clinics } = useClinics();

  const countries = useMemo(() => uniqueSortedCountriesSelector({ clinics }), [clinics]);

  const [viewedClinics, setViewedClinics] = useState<IClinic[]>([]);

  useEffect(() => {
    const viewedClinics = JSON.parse(localStorage.getItem('viewed-clinics') || '[]');

    setViewedClinics(viewedClinics);
  }, []);

  useHandleGeocodeSearchInput();
  useHandleGeocodeCurrentLocation();

  return (
    <div className="flex min-h-[100%] flex-col items-center bg-secondary">
      <div className="h-[217px] w-full bg-search bg-cover bg-center bg-no-repeat md:bg-search-md lg:h-[375px] lg:bg-search-lg"></div>
      <div className="flex w-full flex-col px-[16px] md:px-[60px] lg:max-w-[795px] lg:px-[0px]">
        {isPending ? (
          <div className="mt-[-60px] flex w-full flex-col items-center justify-center bg-white px-[16px] py-[40px] text-center text-primary md:min-h-[335px] md:p-[48px] lg:mt-[-100px]">
            {t('Loading clinics, please wait...')}
          </div>
        ) : isError ? (
          <div className="mt-[-60px] flex w-full flex-col items-center justify-center bg-white px-[16px] py-[40px] text-center text-primary md:min-h-[335px] md:p-[48px] lg:mt-[-100px]">
            <h2 className="text-[1.25em] font-medium text-red-600">
              {t('We are experiencing an issue with loading the clinics. Please try again later.')}
            </h2>
          </div>
        ) : clinics.length === 0 ? (
          <div className="mt-[-60px] flex w-full flex-col items-center justify-center bg-white px-[16px] py-[40px] text-center text-primary md:min-h-[335px] md:p-[48px] lg:mt-[-100px]">
            <h2 className="text-[1.25em] font-medium text-yellow-500">
              {t('We are experiencing an issue with loading the clinics. Please try again later.')}
            </h2>
          </div>
        ) : (
          <>
            <div className="mt-[-60px] flex w-full flex-col bg-white px-[16px] py-[40px] text-center text-primary md:min-h-[335px] md:p-[48px] lg:mt-[-100px]">
              <span className="text-[0.875em] font-medium uppercase tracking-[0.061em] opacity-60">
                {t('Available in {{clinics}} clinics across {{countries}} countries', {
                  clinics: clinics.length,
                  countries: countries.length,
                })}
              </span>

              <span className="mx-auto mt-[16px] max-w-[600px] text-[1.2em] font-light md:text-[1.7em]">
                {t('Join thousands of patients starting their journey with the Allurion Program.')}
              </span>

              <span className="mx-auto mt-[16px] max-w-[600px] text-[1em] font-light md:text-[1em]">
                {t('Find an Allurion clinic near you and book your consultation today.')}
              </span>

              {isValid && (
                <div className="mt-[36px] flex flex-col items-end gap-3 md:flex-row">
                  <AutoComplete
                    className="w-full md:w-[331px] lg:w-[471px]"
                    label={t('Enter a location')}
                  />
                  <Button
                    text={t('Search Clinics')}
                    className="h-[52px] w-full md:w-[211px] location-search-submit"
                    onClick={() => {
                      navigate('/map');
                    }}
                    disabled={!mapViewBounds}
                  />
                </div>
              )}
              <CheckMapAPIKey setIsValid={setIsValid} />
            </div>
            <div className="w-full flex flex-col md:flex-row md:items-center p-[16px] md:ps-[48px] md:pe-[60px] bg-[#D9E5E4] text-primary">
              <span className="mb-[16px] md:mb-0 md:w-3/4 md:pe-[80px]">
                {t(
                  "Not sure if the Allurion Program is right for you? Start with a quick eligibility check to see if you're a good fit - before booking a consultation."
                )}
              </span>

              <a
                href={t('/en/free-consultation')}
                className="uppercase no-underline hover:underline"
              >
                {t('Check Eligibility')}
              </a>
            </div>
            <div className="mt-[48px] flex  w-full flex-col text-center text-primary">
              {viewedClinics.length >= 2 && (
                <div>
                  <h2 className="text-start text-[1.25em] capitalize tracking-[-0.075em] text-primary remove-negative-letter-spacing">
                    {t('Previously viewed clinics:')}
                  </h2>
                  <div className="mt-[15px] flex flex-col  lg:mt-[70px] lg:flex-row">
                    {viewedClinics.map((clinic, i) => {
                      return (
                        <ClinicCard
                          clinic={clinic}
                          key={clinic.API_Id__c}
                          className={`px border-0 border-[#C2CFD1] border-solid px-0 lg:px-[30px] ${
                            i === 0 ? 'lg:border-e' : ''
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="bg-[#D9E5E4] w-full mt-[40px] p-[16px] text-center text-primary text-[0.875em]">
        <span>
          {t(
            'This Clinic Locator is provided by Allurion Technologies as a value-added service. Allurion, at its sole and absolute discretion, may modify the Clinic Locator at any time.'
          )}
        </span>
      </div>
    </div>
  );
}
